import React, { type ReactNode, useEffect, useState } from 'react'
import {
  Box,
  CircularProgress,
  Fab,
  FormControl,
  InputLabel,
  Pagination,
  Skeleton,
  Stack,
  TextField
} from '@mui/material'
import { useSessionContext } from 'session'
import AddIcon from '@mui/icons-material/Add'
import {
  type BaseSlo,
  createSlo,
  deleteSlo,
  type ExistingSlo,
  type NewSlo,
  type SloFilterResults,
  sloListFilter,
  updateSlo
  , type Country
} from 'services'
import {
  SlosFilters, SlosTable,
  AlertDialog,
  CountryAddressSelector,
  CountryMultipleSelector,
  IconButtonWithTooltip,
  OrganizationsTypeSelect,
  skeletonStyles,
  useGlobalMessageHandler,
  useCountriesLuts
  , RequiredFieldHelperText
} from 'components'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import SaveIcon from '@mui/icons-material/Save'
import DialogActions from '@mui/material/DialogActions'
import CloseIcon from '@mui/icons-material/Close'
import { formatErrorFieldMessages } from 'interfaces'

export interface SloFilters {
  type: number
  max_days: number
  min_days: number
  country: Country | null
  start_date: string | null
  end_date: string | null
  page: number
  order_field: string
  order_direction: 'ASC' | 'DESC'

}
export const ServiceLevelObjectivePage: React.FC = () => {
  const { changeModuleName, changeUniqueId } = useSessionContext()
  const [open, setOpen] = useState<boolean>(false)
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false)
  const [updateMode, setUpdateMode] = useState<boolean>(false)
  const { lutValues: countriesLutValues } = useCountriesLuts()
  const newSlo = {
    days: null,
    countries: [],
    orgType: null
  }
  const [managedSlo, setManagedSlo] = useState<BaseSlo>(newSlo)

  const [slos, setSlos] = useState<ExistingSlo[]>([])
  const initialState: SloFilters = {
    type: 0,
    max_days: 360,
    min_days: 0,
    country: null,
    start_date: null,
    end_date: null,
    page: 1,
    order_field: 'id',
    order_direction: 'ASC'
  }
  const [filters, setFilters] = useState<SloFilters>(initialState)
  const [lastQueriedFilters, setLastQueriedFilters] = useState<SloFilters>(initialState)
  const [loadResultWithFilters, setLoadResultWithFilters] = useState(false)
  const [pageCount, setPageCount] = useState<number>(0)
  const [loading, setLoading] = useState<boolean>(false)
  const [saveLoading, setSaveLoading] = useState<boolean>(false)
  const { showSuccessMessage, showErrorMessage } = useGlobalMessageHandler()

  useEffect(() => {
    changeModuleName('SLOs')
    changeUniqueId('')
  }, [changeModuleName])

  const updateFilters = (filters: any): void => {
    setFilters(prevState => ({ ...prevState, ...filters }))
  }

  const cleanFilter = (): void => {
    const diff = JSON.stringify(initialState) === JSON.stringify(lastQueriedFilters)
    setFilters(initialState)
    if (!diff) { setLoadResultWithFilters(prevState => !prevState) }
  }

  const fetchSlos = async (): Promise<SloFilterResults> => {
    setLoading(true)

    try {
      const params: any = {
        StartDate: filters.start_date !== '' ? filters.start_date : undefined,
        EndDate: filters.end_date !== '' ? filters.end_date : undefined,
        MinDays: filters.min_days !== 0 ? filters.min_days : undefined,
        MaxDays: filters.max_days !== 360 ? filters.max_days : undefined,
        Country: filters.country != null ? filters.country.id : undefined,
        OrgType: filters.type !== 0 ? filters.type : undefined,
        page: filters.page !== 0 ? filters.page : undefined
      }
      params.order_by = filters.order_field
      params.order_direction = filters.order_direction
      return await sloListFilter(params)
    } catch (error) {
      console.error(error)
      return { data: [], page_number: 1, page_size: 1, total_pages: 1, total_items: 0 }
    }
  }

  const updateTableSlosWithFilter = async (): Promise<void> => {
    fetchSlos().then(response => {
      setSlos(response.data)
      setPageCount(response.total_pages)
      setLastQueriedFilters(filters)
      if (filters.page !== response.page_number) { updateFilters({ page: response.page_number }) }
    }).catch(reason => {
      console.log(reason)
      setSlos([])
      setPageCount(0)
    }).finally(() => { setLoading(false) })
  }

  useEffect(() => {
    const updateTablePage = async (): Promise<void> => {
      await updateTableSlosWithFilter()
    }
    void updateTablePage()
  }, [loadResultWithFilters])

  function updateOrdering (field: string, direction: string): void {
    updateFilters({ order_field: field, order_direction: direction })
    setLoadResultWithFilters(prevState => !prevState)
  }

  const loadExistingSlo = (existingSlo: ExistingSlo): void => {
    setManagedSlo(existingSlo)
    setUpdateMode(true)
    setOpen(true)
  }

  const openDelete = (existingSlo: ExistingSlo): void => {
    setManagedSlo(existingSlo)
    setOpenDeleteDialog(true)
  }

  const handleDelete = (): void => {
    if (managedSlo.id != null) {
      deleteSlo(managedSlo.id).then((value) => {
        showSuccessMessage('SLO Deleted')
        void updateTableSlosWithFilter()
        setOpenDeleteDialog(false)
      }).catch(() => { showErrorMessage('Error Deleting SLO') })
    }
  }
  const handleSave = (): void => {
    if (updateMode) {
      setSaveLoading(true)
      const updatingSlo = managedSlo as ExistingSlo
      updateSlo(updatingSlo).then((value) => {
        if (value.succeeded) {
          setOpen(false)
          showSuccessMessage('SLO Updated')
          void updateTableSlosWithFilter()
        } else {
          showErrorMessage(formatErrorFieldMessages(value.errors))
        }
      }).catch(() => { showErrorMessage('Error Updating SLO') })
        .finally(() => {
          setSaveLoading(false)
        }
        )
    } else {
      setSaveLoading(true)
      const newSlo = managedSlo as NewSlo
      createSlo(newSlo).then((value) => {
        if (value.succeeded) {
          setOpen(false)
          showSuccessMessage('SLO Created')
          void updateTableSlosWithFilter()
        } else {
          showErrorMessage(formatErrorFieldMessages(value.errors))
        }
      }).catch(() => { showErrorMessage('Error Creating SLO') })
        .finally(() => {
          setSaveLoading(false)
        }
        )
    }
  }

  const isFieldEmptyOrInvalid = (field: string, isNumeric: boolean = false): boolean => {
    const isEmptyOrInvalid = managedSlo[field as keyof BaseSlo] == null || managedSlo[field as keyof BaseSlo] === ''
    if (isNumeric) {
      return isNaN(managedSlo[field as keyof BaseSlo] as number) || isEmptyOrInvalid
    } else return isEmptyOrInvalid
  }
  const requiredFields = ['days', 'orgType']

  const areFieldsValid = (): boolean => {
    const baseFieldsValid = requiredFields.every((field) => !isFieldEmptyOrInvalid(field))
    let countriesValid = false
    if (!isMultipleCountries()) {
      countriesValid = managedSlo.country != null
    } else {
      countriesValid = (managedSlo.countries?.length ?? 0) > 0
    }
    return baseFieldsValid && countriesValid
  }

  const isMultipleCountries = (): boolean => {
    return 'countries' in managedSlo
  }

  const renderUpdateSlo = (): ReactNode => {
    return (
        <Dialog fullWidth maxWidth="xl" onClose={() => { setOpen(false) }} open={open}>
          <DialogTitle align={'center'}>
            {!updateMode ? 'New SLO' : 'Update SLO'}
          </DialogTitle>
          <DialogContent>
              <Stack mt={1} spacing={1}>
                {loading
                  ? (
                        <>
                          {Array.from({ length: 3 }).map((_, index) => (
                              <Skeleton key={index} variant="rectangular" sx={skeletonStyles} />
                          ))}
                        </>
                    )
                  : (
                        <>
                        <FormControl fullWidth={true} variant="outlined">
                          <TextField
                              label="Days"
                              type="number"
                              value={managedSlo.days}
                              inputProps={{
                                min: 0
                              }}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const numberValue = parseInt(e.target.value)
                                setManagedSlo(prevState => ({ ...prevState, days: !isNaN(numberValue) && numberValue > 0 ? numberValue : null }))
                              } }
                              variant="outlined" />
                          <RequiredFieldHelperText isEmpty={isFieldEmptyOrInvalid('days', true)}>
                            This field is required and must be a numeric value above zero.
                          </RequiredFieldHelperText>
                        </FormControl>
                          <FormControl variant="outlined" fullWidth={true}>
                            <InputLabel id="type-select-label">Type</InputLabel>
                            <OrganizationsTypeSelect
                                id={'type-select-2'}
                                filters={managedSlo}
                                disabled={updateMode}
                                updateFilters={(filters) => { setManagedSlo(prevState => ({ ...prevState, orgType: filters.type === 0 ? null : { id: filters.type } })) } } />
                            <RequiredFieldHelperText isEmpty={isFieldEmptyOrInvalid('orgType')}>
                              This field is required
                            </RequiredFieldHelperText>
                          </FormControl>
                        <FormControl variant="outlined" fullWidth={true}>
                          {isMultipleCountries() && managedSlo.countries != null &&
                              <>
                                <CountryMultipleSelector handleCountryChange={(countries) => { setManagedSlo(prevState => ({ ...prevState, countries })) }} value={managedSlo.countries} />
                                <RequiredFieldHelperText isEmpty={(managedSlo.countries?.length ?? 0) <= 0}>
                                  This field is required, select at least 1 country.
                                </RequiredFieldHelperText>
                              </>
                          }
                          {!isMultipleCountries() && managedSlo.country !== undefined &&
                              <>
                                <CountryAddressSelector
                                    countries={countriesLutValues.countries}
                                    value={managedSlo.country}
                                    setValue={(e) => { setManagedSlo(prevState => ({ ...prevState, country: e })) }}
                                    disabled={updateMode}
                                />
                                <RequiredFieldHelperText isEmpty={isFieldEmptyOrInvalid('country')}>
                                  This field is required
                                </RequiredFieldHelperText>
                              </>}
                        </FormControl>
                        </>
                    )}
              </Stack>
            </DialogContent>
            <DialogActions>
              <IconButtonWithTooltip label="Cancel" color="secondary" onClick={() => { setOpen(false) }} disabled={loading} icon={<CloseIcon/>} />
              {!saveLoading ? <IconButtonWithTooltip label="Save SLO" color="primary" onClick={handleSave} disabled={loading || !areFieldsValid()} icon={<SaveIcon/>} /> : <CircularProgress/>}
            </DialogActions>
        </Dialog>
    )
  }

  return (
        <Box>
          <AlertDialog
              onClose={() => { setOpenDeleteDialog(false) }}
              open={openDeleteDialog}
              onButtonClick={handleDelete}
              title='Confirm SLO deletion'
              contentText='Are you sure you want to delete this SLO? This action cannot be reversed.'
              nameButtonCancel='Close'
              nameButtonConfirm='Confirm'
          />
          {renderUpdateSlo()}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <Box width='95%' sx={{ pb: 1 }}>
                    <SlosFilters doFilter={updateTableSlosWithFilter} filters={filters} updateFilters={updateFilters} cleanFilters={cleanFilter}/>
                    <Box sx={{ display: 'flex', justifyContent: 'end', mb: 2 }}>
                        <Fab color="primary" aria-label="add" onClick={() => {
                          setManagedSlo(newSlo)
                          setOpen(true)
                          setUpdateMode(false)
                        }}>
                            <AddIcon/>
                        </Fab>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    {loading && <CircularProgress sx={{ alignSelf: 'center', mb: 3 }}/>}
                    {!loading && <SlosTable slos={slos} orderField={filters.order_field} orderDirection={filters.order_direction} updateOrdering={updateOrdering} openEdit={loadExistingSlo} openDelete={openDelete}/>}
                    </Box>
                </Box>
                <Pagination page={filters.page} count={pageCount} onChange={(event, page) => {
                  updateFilters({ page })
                  setLoadResultWithFilters(prevState => !prevState)
                }}/>
            </Box>
        </Box>
  )
}
